import JSZip from 'jszip';
import XlsxPopulate from 'xlsx-populate';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';
import { OutputOrderData } from '../domains/entities/orderResponse';
import { Order } from '@/coreDomainsModule/entities/order';
import { saveAs } from 'file-saver';
import type { OrderTemplateDetailEntity } from '@/orderTemplateModule/domains/entities/orderTemplate';
import { convertIDateToDateFormat, convertUnixTimeToIDate } from '@/corePagesModule/BaseTableContent/utils/dateFormat';
import { loadMyPropertyPreferences, myPropertyPreferences } from '@/appLogic/view/preferences';

/**
 * B列～L列などに対応する列マッピング用インターフェース
 */
interface IColumnMapping {
  dataKey: keyof OutputOrderData; // データのキー
  colIndex: number; // 1-basedの列番号 (B列=2, C列=3, ...)
  headerLabel: string; // ヘッダー文字列(必要なら)
  headerRow?: number; // ヘッダーを書く行(必要なら)
}

const t = i18n.t;

/**
 * 確定発注エクセルを作成するためのユースケース
 */
export class DownloadOrderExcelUseCase {
  private static readonly t = i18n.t;

  /**
   * 1) A列～R列の書き込み定義をまとめておく
   *    (ヘッダー行を書くなら headerRow で指定)
   */
  private static readonly columnMappings: IColumnMapping[] = [
    { dataKey: 'orderId', colIndex: 1, headerLabel: 'LP発注番号', headerRow: 2 },
    { dataKey: 'createdOrderDate', colIndex: 2, headerLabel: '発注日', headerRow: 2 },
    { dataKey: 'orderAmount', colIndex: 3, headerLabel: '発注数量', headerRow: 2 },
    { dataKey: 'desiredDeliveryDate', colIndex: 4, headerLabel: '希望納期', headerRow: 2 },
    { dataKey: 'estimatedTimeOfArrival', colIndex: 5, headerLabel: '納期', headerRow: 2 },
    { dataKey: 'warehouseNumber', colIndex: 6, headerLabel: '置場CD', headerRow: 2 },
    { dataKey: 'warehouse', colIndex: 7, headerLabel: '置場名', headerRow: 2 },
    { dataKey: 'supplierNumber', colIndex: 8, headerLabel: '仕入先CD', headerRow: 2 },
    { dataKey: 'supplierName', colIndex: 9, headerLabel: '仕入先名', headerRow: 2 },
    { dataKey: 'manufacturer', colIndex: 10, headerLabel: '製造社', headerRow: 2 },
    { dataKey: 'businessNumber', colIndex: 11, headerLabel: '商品CD', headerRow: 2 },
    { dataKey: 'businessName', colIndex: 12, headerLabel: '商品名', headerRow: 2 }, // NOTE: ヘッダー名は現状の仕様だと空に
    { dataKey: 'package', colIndex: 13, headerLabel: '容量', headerRow: 2 },
    { dataKey: 'unit', colIndex: 14, headerLabel: '単位', headerRow: 2 },
    { dataKey: 'orderNumber', colIndex: 15, headerLabel: 'ユーザー発注番号', headerRow: 2 },
    { dataKey: 'aggregateBusinessResponsibleUsers', colIndex: 16, headerLabel: '仕入担当者', headerRow: 2 },
    { dataKey: 'orderNote', colIndex: 17, headerLabel: 'メモ', headerRow: 2 },
    { dataKey: 'userCustomParameters', colIndex: 18, headerLabel: 'ユーザー固有番号', headerRow: 2 },
    { dataKey: 'orderGroupName', colIndex: 19, headerLabel: 'グループ名', headerRow: 2 },
    { dataKey: 'invoiceNumber', colIndex: 20, headerLabel: 'インボイス番号', headerRow: 2 },
    { dataKey: 'destinationName', colIndex: 21, headerLabel: '仕向地', headerRow: 2 },
    { dataKey: 'orderIssuedNumber', colIndex: 22, headerLabel: '発注管理番号', headerRow: 2 },
  ];

  /**
   * 発注エクセルを生成して、ファイルのダウンロードを行う関数
   * @param excelData - エクセル作成に必要なデータ
   * @param fileBlob - テンプレートエクセルの Blob
   * @param templateDetail - カスタムテンプレート詳細
   */
  async Execute(excelData: OutputOrderData[], fileBlob: Blob, templateDetail?: OrderTemplateDetailEntity | null): Promise<void | Error> {
    await loadMyPropertyPreferences();
    if (templateDetail) {
      const originalName = templateDetail.metadata.fileOriginalName || 'order_template.xlsx';
      const normalizedOriginalName = originalName.toLowerCase().endsWith('.xlsx') ? originalName : `${originalName}.xlsx`;

      const chunkSize =
        templateDetail.metadata.maxOrdersPerSheet && templateDetail.metadata.maxOrdersPerSheet > 0
          ? templateDetail.metadata.maxOrdersPerSheet
          : excelData.length;
      const orderChunks = DownloadOrderExcelUseCase.chunkOrders(excelData, chunkSize);

      if (orderChunks.length === 0) {
        return new Error('出力対象の発注がありません');
      }

      if (orderChunks.length === 1) {
        const workbook = await XlsxPopulate.fromDataAsync(fileBlob);
        DownloadOrderExcelUseCase.fillCustomTemplate(workbook, orderChunks[0], templateDetail);
        const buffer = await workbook.outputAsync();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, normalizedOriginalName);
        return;
      }

      const zip = new JSZip();
      const baseZipName = normalizedOriginalName.replace(/\.xlsx$/i, '');
      await Promise.all(
        orderChunks.map(async (chunk, index) => {
          const workbook = await XlsxPopulate.fromDataAsync(fileBlob);
          DownloadOrderExcelUseCase.fillCustomTemplate(workbook, chunk, templateDetail);
          const buffer = await workbook.outputAsync();
          const fileName = DownloadOrderExcelUseCase.normalizeZipEntryName(`${baseZipName}（${index + 1}/${orderChunks.length}）.xlsx`);
          zip.file(fileName, buffer, { binary: true });
        })
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `${baseZipName}.zip`;
      saveAs(zipBlob, zipFileName);
      return;
    }

    const workbook = await XlsxPopulate.fromDataAsync(fileBlob);
    DownloadOrderExcelUseCase.fillDefaultTemplate(workbook, excelData);
    const excelArrayBuffer = await workbook.outputAsync();
    const blob = new Blob([excelArrayBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `${t('確定発注出力', { date: DateService.getTodayString() })}.xlsx`);
    return;
  }

  /**
   * 既存テンプレートに従ってデータを書き込む
   * @param workbook - 生成対象のWorkbook
   * @param excelData - 発注データ
   */
  private static fillDefaultTemplate(workbook: XlsxPopulate.Workbook, excelData: OutputOrderData[]): void {
    const sheet = workbook.sheet(0);

    const headerFill = sheet.cell(2, 1).style('fill');
    const headerColor =
      headerFill && typeof headerFill === 'object' && 'color' in headerFill && headerFill.color ? (headerFill.color as string) : '#FFFFFF';
    let headerHeight = 20;
    try {
      headerHeight = sheet.row(2).height() ?? 20;
    } catch {
      headerHeight = 20;
    }

    sheet.cell(2, 1).style('fill', { type: 'solid', color: headerColor });
    sheet.cell(2, 1).style('border', { style: 'thin' });

    DownloadOrderExcelUseCase.columnMappings.forEach((mapping) => {
      if (mapping.headerRow) {
        sheet.cell(mapping.headerRow, mapping.colIndex).value(DownloadOrderExcelUseCase.t(mapping.headerLabel));
      }
    });

    excelData.forEach((data, idx) => {
      const row = 3 + idx;
      DownloadOrderExcelUseCase.columnMappings.forEach((mapping) => {
        const value = DownloadOrderExcelUseCase._getOutputOrderValue(data, mapping.dataKey);
        sheet.cell(row, mapping.colIndex).value(DownloadOrderExcelUseCase.formatCellValue(value));
      });
    });

    if (excelData.length > 0) {
      const userInputStartRow = 3;
      const userInputEndRow = 3 + excelData.length - 1;
      const userInputStartCol = 1;
      const userInputEndCol = 22;

      sheet.range(userInputStartRow, userInputStartCol, userInputEndRow, userInputEndCol).style('border', { style: 'thin' });
      for (let i = userInputStartRow; i <= userInputEndRow; i++) {
        try {
          sheet.row(i).height(headerHeight);
        } catch {
          // Skip if height setting fails
        }
      }
    }
  }

  /**
   * ユーザー設定テンプレートに従ってデータを書き込む
   * @param workbook - 生成対象のWorkbook
   * @param excelData - 発注データ（テンプレートが許容する件数に分割済み）
   * @param templateDetail - テンプレート詳細
   */
  private static fillCustomTemplate(
    workbook: XlsxPopulate.Workbook,
    excelData: OutputOrderData[],
    templateDetail: OrderTemplateDetailEntity
  ): void {
    if (!templateDetail.mappings.length) {
      return;
    }

    const sheetName = templateDetail.metadata.defaultSheetName;
    const sheetByName = sheetName ? workbook.sheet(sheetName) : null;
    const sheet = sheetByName ?? workbook.sheet(0);

    const parsedMappings = templateDetail.mappings.map((mapping) => {
      const { row, col } = DownloadOrderExcelUseCase.parseCellAddress(mapping.startCell);
      return {
        fieldKey: mapping.fieldKey,
        startRow: row,
        startCol: col,
      };
    });

    excelData.forEach((order, index) => {
      parsedMappings.forEach((mapping) => {
        const targetRow = mapping.startRow + index;
        const value = DownloadOrderExcelUseCase._getOutputOrderValue(order, mapping.fieldKey);
        sheet.cell(targetRow, mapping.startCol).value(DownloadOrderExcelUseCase.formatCellValue(value));
      });
    });
  }

  /**
   * データを指定件数ごとに分割する
   * @param orders - 出力対象の発注データ
   * @param chunkSize - 分割後の最大件数
   */
  private static chunkOrders(orders: OutputOrderData[], chunkSize: number): OutputOrderData[][] {
    if (chunkSize <= 0) {
      return [orders];
    }
    const result: OutputOrderData[][] = [];
    for (let i = 0; i < orders.length; i += chunkSize) {
      result.push(orders.slice(i, i + chunkSize));
    }
    return result;
  }

  /**
   * 指定されたキーに基づいて発注データの値を取得するヘルパー関数
   * @param order - 発注データ
   * @param key - 取得したいデータのキー
   */
  private static _getOutputOrderValue = (order: OutputOrderData, key: string): string | number => {
    const record = order as Record<string, any>;
    switch (key) {
      case 'orderId':
        // 発注ID (LP発注番号)
        return order.orderId ?? '-';
      case 'createdOrderDate':
        // 発注日
        return record[key] ? convertIDateToDateFormat(convertUnixTimeToIDate(record[key]), myPropertyPreferences.outputDateFormat) : '-';
      case 'desiredDeliveryDate':
        // 希望納期
        return convertIDateToDateFormat(convertUnixTimeToIDate(record[key]), myPropertyPreferences.outputDateFormat);
      case 'estimatedTimeOfArrival':
        // 納入日
        return convertIDateToDateFormat(convertUnixTimeToIDate(record[key]), myPropertyPreferences.outputDateFormat);
      case 'expectedShippingDate':
        // 出荷予定日（= 希望納期）
        const time = record['desiredDeliveryDate'];
        if (!time || typeof time !== 'number') return '-';
        return convertIDateToDateFormat(DateService.convertDateToIDate(new Date(time)), myPropertyPreferences.outputDateFormat);
      case 'receiveOrderDate':
        // 受注日（出力を行う当日の日付）
        return convertIDateToDateFormat(DateService.convertDateToIDate(new Date()), myPropertyPreferences.outputDateFormat);
      case 'createDate':
        // 作成日（出力を行う当日の日付）
        return convertIDateToDateFormat(DateService.convertDateToIDate(new Date()), myPropertyPreferences.outputDateFormat);
      case 'userCustomParameters':
        // 顧客台帳ID
        return order.userCustomParameters?.['顧客台帳ID'] ?? '-';
      case 'quantity':
        // 個数
        if (order.orderAmount === undefined || order.orderAmount === 0 || order.package === undefined || order.package === 0) {
          return '-';
        }
        // 個数 = 数量 / 容量
        if (typeof order.orderAmount === 'number' && typeof order.package === 'number' && order.package !== 0) {
          return order.orderAmount / order.package;
        }
        if (typeof record.orderAmount?.divideIntegersToFixed === 'function') {
          return record.orderAmount.divideIntegersToFixed(order.package);
        }
        return '-';
      case 'aggregateBusinessResponsibleUsers':
        // 仕入担当者
        if (!order.aggregateBusinessResponsibleUsers || order.aggregateBusinessResponsibleUsers.length === 0) {
          return '-';
        }
        return order.aggregateBusinessResponsibleUsers.map((user) => user.name).join(', ') || '-';
      case 'orderNote':
        // 発注メモ
        // メモが空の場合はハイフンを返す
        if (!order.orderNote) return '-';
        // メモが空でない場合はそのまま返す
        return order.orderNote ?? '-';
      default:
        return record[key] ?? '-';
    }
  };

  /**
   * セル番地を行・列に変換する
   * @param address - Excel形式のセル番地
   * @returns 行・列番号（1-based）
   */
  private static parseCellAddress(address: string): { row: number; col: number } {
    const trimmed = address.trim().toUpperCase();
    const match = /^([A-Z]+)(\d+)$/.exec(trimmed);
    if (!match) {
      throw new Error(`セル番地の解析に失敗しました: ${address}`);
    }
    const [, colLetters, rowNumber] = match;
    let col = 0;
    for (let i = 0; i < colLetters.length; i++) {
      col = col * 26 + (colLetters.charCodeAt(i) - 64);
    }
    return { row: Number(rowNumber), col };
  }

  /**
   * Excelに書き込む値を整形する
   * @param value - 書き込み対象の値
   */
  private static formatCellValue(value: unknown): string | number {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'boolean') {
      return value ? '1' : '0';
    }
    return String(value);
  }

  /**
   * Zip内のファイル名で階層が解釈されないよう安全な名前へ置換する
   * @param entryName - Zipエントリ名
   */
  private static normalizeZipEntryName(entryName: string): string {
    return entryName.replace(/[\\\/]/g, '_');
  }
}
